<template>
  <div class="comp-lease-vs-purchase-tab">
    <div class="comp-lease-vs-purchase-tab__header">
      <div class="comp-lease-vs-purchase-tab__header-icon">
        <theme-icon name="briefcase-icon" type="icons" classes="w-6 h-6" />
      </div>
      <div>
        <h2 class="comp-lease-vs-purchase-tab__title">Lease vs. Purchase Calculator</h2>
        <p class="comp-lease-vs-purchase-tab__subtitle">Compare leasing vs. purchasing costs and determine equivalent lease value</p>
      </div>
    </div>

    <!-- Metric Cards -->
    <div class="comp-lease-vs-purchase-tab__metrics">
      <div class="comp-lease-vs-purchase-tab__metric-card comp-lease-vs-purchase-tab__metric-card--primary">
        <div class="comp-lease-vs-purchase-tab__metric-icon">
          <theme-icon name="home" type="icons" classes="w-5 h-5" />
        </div>
        <div>
          <div class="comp-lease-vs-purchase-tab__metric-label">Purchase Grand Total</div>
          <div class="comp-lease-vs-purchase-tab__metric-value">{{ formatCurrency(grandTotal) }}</div>
        </div>
      </div>

      <div class="comp-lease-vs-purchase-tab__metric-card comp-lease-vs-purchase-tab__metric-card--primary">
        <div class="comp-lease-vs-purchase-tab__metric-icon">
          <theme-icon name="money" type="icons" classes="w-5 h-5" />
        </div>
        <div>
          <div class="comp-lease-vs-purchase-tab__metric-label">Equivalent Lease (Annual)</div>
          <div class="comp-lease-vs-purchase-tab__metric-value">{{ formatCurrency(equivalentLease) }}</div>
        </div>
      </div>

      <div class="comp-lease-vs-purchase-tab__metric-card comp-lease-vs-purchase-tab__metric-card--primary">
        <div class="comp-lease-vs-purchase-tab__metric-icon">
          <theme-icon name="arrow-up" type="icons" classes="w-5 h-5" />
        </div>
        <div>
          <div class="comp-lease-vs-purchase-tab__metric-label">Equity Required</div>
          <div class="comp-lease-vs-purchase-tab__metric-value">{{ formatCurrency(equityRequired) }}</div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="comp-lease-vs-purchase-tab__content">
      <!-- Purchase Calculation -->
      <div class="comp-lease-vs-purchase-tab__section comp-lease-vs-purchase-tab__section--purchase">
        <div class="comp-lease-vs-purchase-tab__section-header">
          <theme-icon name="home" type="icons" classes="w-4 h-4" />
          <h3 class="comp-lease-vs-purchase-tab__section-title">Purchase Calculation</h3>
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group">
          <label class="comp-lease-vs-purchase-tab__label">TOTAL SQ. FT.</label>
          <div class="comp-lease-vs-purchase-tab__readonly-value">{{ comp.building.total_square_footage | number }} PSF</div>
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group">
          <label class="comp-lease-vs-purchase-tab__label">SALE PRICE PSF</label>
          <themed-input
            v-model="salePrice"
            type="text"
            placeholder="$0.00"
          />
          <div class="comp-lease-vs-purchase-tab__calc-value">{{ formatCurrency(salePricePSF, 2) }}</div>
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group">
          <label class="comp-lease-vs-purchase-tab__label">SALE DISCOUNT %</label>
          <themed-input
            v-model="saleDiscountPercentage"
            type="text"
            placeholder="0%"
          />
          <div class="comp-lease-vs-purchase-tab__calc-value">{{ formatCurrency(salePriceMinusDiscount) }}</div>
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group">
          <label class="comp-lease-vs-purchase-tab__label">ECO DEV DISCOUNT %</label>
          <themed-input
            v-model="economicDeveloperDiscountPercentage"
            type="text"
            placeholder="0%"
          />
          <div class="comp-lease-vs-purchase-tab__calc-value">{{ formatCurrency(salePriceMinusEcoDevDiscount) }}</div>
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group comp-lease-vs-purchase-tab__form-group--highlight">
          <label class="comp-lease-vs-purchase-tab__label">ADJUSTED SALE PRICE</label>
          <div class="comp-lease-vs-purchase-tab__readonly-value">{{ formatCurrency(adjustedSalePrice) }}</div>
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group">
          <label class="comp-lease-vs-purchase-tab__label">CLOSING COST %</label>
          <themed-input
            v-model="closingCostPercentage"
            type="text"
            placeholder="0%"
          />
          <div class="comp-lease-vs-purchase-tab__calc-value">{{ formatCurrency(closingCosts) }}</div>
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group">
          <label class="comp-lease-vs-purchase-tab__label">SUBTOTAL</label>
          <div class="comp-lease-vs-purchase-tab__readonly-value">{{ formatCurrency(subtotal) }}</div>
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group">
          <label class="comp-lease-vs-purchase-tab__label">RENOVATION COSTS</label>
          <themed-input
            v-model="renovationCosts"
            type="text"
            placeholder="$0"
          />
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group">
          <label class="comp-lease-vs-purchase-tab__label">OTHER COSTS</label>
          <themed-input
            v-model="otherCosts"
            type="text"
            placeholder="$0"
          />
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group comp-lease-vs-purchase-tab__form-group--total">
          <label class="comp-lease-vs-purchase-tab__label">Grand Total</label>
          <div class="comp-lease-vs-purchase-tab__total-value">{{ formatCurrency(grandTotal) }}</div>
        </div>
      </div>

      <!-- Combined: Debt Service, Equity & Lease Calculation -->
      <div class="comp-lease-vs-purchase-tab__section comp-lease-vs-purchase-tab__section--combined">
        <!-- Debt Service -->
        <div class="comp-lease-vs-purchase-tab__section-header">
          <theme-icon name="money" type="icons" classes="w-4 h-4" />
          <h3 class="comp-lease-vs-purchase-tab__section-title">Debt Service</h3>
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group">
          <label class="comp-lease-vs-purchase-tab__label">INTEREST RATE %</label>
          <themed-input
            v-model="debtServiceInterestPercentage"
            type="text"
            placeholder="0%"
          />
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group">
          <label class="comp-lease-vs-purchase-tab__label">TERM (YEARS)</label>
          <themed-input
            v-model="debtServiceLeaseTermYears"
            type="number"
            placeholder="0"
          />
        </div>

        <div class="comp-lease-vs-purchase-tab__divider"></div>

        <!-- Amount of Equity Required -->
        <div class="comp-lease-vs-purchase-tab__section-header">
          <theme-icon name="arrow-up" type="icons" classes="w-4 h-4" />
          <h3 class="comp-lease-vs-purchase-tab__section-title">Amount of Equity Required</h3>
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group">
          <label class="comp-lease-vs-purchase-tab__label">EQUITY %</label>
          <themed-input
            v-model="equityPercentage"
            type="text"
            placeholder="0%"
          />
          <div class="comp-lease-vs-purchase-tab__calc-value">{{ formatCurrency(equityRequired) }}</div>
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group">
          <label class="comp-lease-vs-purchase-tab__label">DEBT</label>
          <div class="comp-lease-vs-purchase-tab__readonly-value">{{ formatCurrency(debtTotal) }}</div>
        </div>

        <div class="comp-lease-vs-purchase-tab__divider"></div>

        <!-- Lease Calculation -->
        <div class="comp-lease-vs-purchase-tab__section-header">
          <theme-icon name="document-text" type="icons" classes="w-4 h-4" />
          <h3 class="comp-lease-vs-purchase-tab__section-title">Lease Calculation</h3>
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group">
          <label class="comp-lease-vs-purchase-tab__label">DEBT COST (ANNUAL)</label>
          <div class="comp-lease-vs-purchase-tab__readonly-value">{{ formatCurrency(debtCost) }}</div>
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group">
          <label class="comp-lease-vs-purchase-tab__label">OPERATING EXPENSES</label>
          <themed-input
            v-model="operatingExpenses"
            type="text"
            placeholder="$0"
          />
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group">
          <label class="comp-lease-vs-purchase-tab__label">TAXES</label>
          <themed-input
            v-model="taxes"
            type="text"
            placeholder="$0"
          />
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group">
          <label class="comp-lease-vs-purchase-tab__label">SUBTOTAL</label>
          <div class="comp-lease-vs-purchase-tab__readonly-value">{{ formatCurrency(debtSubtotal) }}</div>
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group">
          <label class="comp-lease-vs-purchase-tab__label">ROI ON LL EQUITY %</label>
          <themed-input
            v-model="roiOnLLEquityPercentage"
            type="text"
            placeholder="0%"
          />
          <div class="comp-lease-vs-purchase-tab__calc-value">{{ formatCurrency(roiOnLLEquity) }}</div>
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group">
          <label class="comp-lease-vs-purchase-tab__label">AMORTIZED DOWN</label>
          <themed-input
            v-model="amortizedDownPayment"
            type="text"
            placeholder="$0"
          />
        </div>

        <div class="comp-lease-vs-purchase-tab__form-group comp-lease-vs-purchase-tab__form-group--total">
          <label class="comp-lease-vs-purchase-tab__label">Equivalent Lease $</label>
          <div class="comp-lease-vs-purchase-tab__total-value">{{ formatCurrency(equivalentLease) }}</div>
        </div>
      </div>
    </div>

    <!-- Save Button -->
    <div class="comp-lease-vs-purchase-tab__actions">
      <themed-button
        @click="save"
        variant="primary"
        class="comp-lease-vs-purchase-tab__save-button"
      >
        Save Calculator
      </themed-button>
    </div>
  </div>
</template>

<script>
import ThemedButton from '../ThemedButton.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'CompLeaseVsPurchaseTab',

  components: {
    ThemedButton,
    ThemedInput,
    ThemeIcon
  },

  props: {
    compData: {
      type: [Array, Object],
      required: true
    },

    calculationData: {
      type: [Array, Object],
      required: true
    }
  },

  data() {
    const calcData = this.calculationData || {};
    return {
      salePrice: calcData.sale_price || 0,
      saleDiscountPercentage: calcData.sale_discount_percentage || 0,
      economicDeveloperDiscountPercentage: calcData.economic_developer_discount_percentage || 0,
      closingCostPercentage: calcData.closing_cost_percentage || 0,
      renovationCosts: calcData.renovation_costs || 0,
      otherCosts: calcData.other_costs || 0,
      debtServiceInterestPercentage: calcData.debt_service_interest_percentage || 0,
      debtServiceLeaseTermYears: calcData.debt_service_lease_term_years || 0,
      equityPercentage: calcData.equity_percentage || 0,
      operatingExpenses: calcData.operating_expenses || 0,
      taxes: calcData.taxes || 0,
      roiOnLLEquityPercentage: calcData.roi_on_ll_equity_percentage || 0,
      amortizedDownPayment: calcData.amortized_down_payment || 0
    };
  },

  computed: {
    comp() {
      return this.compData;
    },

    salePriceCleansed() {
      const cleansed = Number.prototype.cleanseInteger(this.salePrice);
      return isNaN(cleansed) ? 0 : cleansed;
    },

    salePricePSF() {
      return isNaN(this.salePriceCleansed) || this.comp.building.total_square_footage == 0
        ? 0
        : (this.salePriceCleansed / this.comp.building.total_square_footage) * 100;
    },

    saleDiscountPercentageCleansed() {
      const cleansed = Number.prototype.cleanseInteger(this.saleDiscountPercentage);
      return isNaN(cleansed) ? 0 : cleansed / 100;
    },

    salePriceMinusDiscount() {
      return isNaN(this.salePriceCleansed) ? 0 : (this.salePriceCleansed * this.saleDiscountPercentageCleansed) * 100;
    },

    salePriceMinusDiscountPSF() {
      return isNaN(this.salePriceMinusDiscount) || this.comp.building.total_square_footage == 0
        ? 0
        : this.salePriceMinusDiscount / this.comp.building.total_square_footage;
    },

    economicDeveloperDiscountPercentageCleansed() {
      const cleansed = Number.prototype.cleanseInteger(this.economicDeveloperDiscountPercentage);
      return isNaN(cleansed) ? 0 : cleansed / 100;
    },

    salePriceMinusEcoDevDiscount() {
      return isNaN(this.salePriceCleansed) ? 0 : (this.salePriceCleansed * this.economicDeveloperDiscountPercentageCleansed) * 100;
    },

    salePriceMinusEcoDevDiscountPSF() {
      return isNaN(this.salePriceMinusEcoDevDiscount) || this.comp.building.total_square_footage == 0
        ? 0
        : this.salePriceMinusEcoDevDiscount / this.comp.building.total_square_footage;
    },

    adjustedSalePrice() {
      return (this.salePriceCleansed - this.salePriceMinusDiscount / 100 - this.salePriceMinusEcoDevDiscount / 100) * 100;
    },

    adjustedSalePricePSF() {
      return isNaN(this.adjustedSalePrice) || this.comp.building.total_square_footage == 0
        ? 0
        : this.adjustedSalePrice / this.comp.building.total_square_footage;
    },

    closingCostPercentageCleansed() {
      const cleansed = Number.prototype.cleanseInteger(this.closingCostPercentage);
      return isNaN(cleansed) ? 0 : cleansed / 100;
    },

    closingCosts() {
      return (this.closingCostPercentageCleansed * this.salePriceCleansed) * 100;
    },

    closingCostsPSF() {
      return isNaN(this.closingCosts) || this.comp.building.total_square_footage == 0
        ? 0
        : this.closingCosts / this.comp.building.total_square_footage;
    },

    subtotal() {
      return this.adjustedSalePrice + this.closingCosts;
    },

    subtotalPSF() {
      return isNaN(this.subtotal) || this.comp.building.total_square_footage == 0
        ? 0
        : this.subtotal / this.comp.building.total_square_footage;
    },

    renovationCostsCleansed() {
      const cleansed = Number.prototype.cleanseInteger(this.renovationCosts);
      return isNaN(cleansed) ? 0 : cleansed;
    },

    renovationCostsPSF() {
      return isNaN(this.renovationCostsCleansed) || this.comp.building.total_square_footage == 0
        ? 0
        : (this.renovationCostsCleansed / this.comp.building.total_square_footage) * 100;
    },

    otherCostsCleansed() {
      const cleansed = Number.prototype.cleanseInteger(this.otherCosts);
      return isNaN(cleansed) ? 0 : cleansed;
    },

    otherCostsPSF() {
      return isNaN(this.otherCostsCleansed) || this.comp.building.total_square_footage == 0
        ? 0
        : (this.otherCostsCleansed / this.comp.building.total_square_footage) * 100;
    },

    grandTotal() {
      return this.subtotal + this.renovationCostsCleansed * 100 + this.otherCostsCleansed * 100;
    },

    grandTotalPSF() {
      return isNaN(this.grandTotal) || this.comp.building.total_square_footage == 0
        ? 0
        : this.grandTotal / this.comp.building.total_square_footage;
    },

    interestPercentageCleansed() {
      const cleansed = Number.prototype.cleanseInteger(this.debtServiceInterestPercentage);
      return isNaN(cleansed) ? 0 : cleansed / 100;
    },

    leaseTermMonths() {
      return isNaN(this.debtServiceLeaseTermYears) ? 0 : this.debtServiceLeaseTermYears * 12;
    },

    equityPercentageCleansed() {
      const cleansed = Number.prototype.cleanseInteger(this.equityPercentage);
      return isNaN(cleansed) ? 0 : cleansed / 100;
    },

    equityRequired() {
      return this.equityPercentageCleansed * this.grandTotal;
    },

    equityRequiredPSF() {
      return isNaN(this.equityRequired) || this.comp.building.total_square_footage == 0
        ? 0
        : this.equityRequired / this.comp.building.total_square_footage;
    },

    debtTotal() {
      return this.grandTotal - this.equityRequired;
    },

    debtTotalPSF() {
      return isNaN(this.debtTotal) || this.comp.building.total_square_footage == 0
        ? 0
        : this.debtTotal / this.comp.building.total_square_footage;
    },

    debtCost() {
      const pmt = Number.prototype.pmt(this.debtTotal, Number.prototype.cleanseInteger(this.debtServiceInterestPercentage), this.leaseTermMonths);
      return pmt * 12;
    },

    debtCostPSF() {
      return isNaN(this.debtCost) || this.comp.building.total_square_footage == 0
        ? 0
        : this.debtCost / this.comp.building.total_square_footage;
    },

    operatingExpensesCleansed() {
      const cleansed = Number.prototype.cleanseInteger(this.operatingExpenses);
      return isNaN(cleansed) ? 0 : cleansed;
    },

    operatingExpensesPSF() {
      return isNaN(this.operatingExpensesCleansed) || this.comp.building.total_square_footage == 0
        ? 0
        : (this.operatingExpensesCleansed / this.comp.building.total_square_footage) * 100;
    },

    taxesCleansed() {
      const cleansed = Number.prototype.cleanseInteger(this.taxes);
      return isNaN(cleansed) ? 0 : cleansed;
    },

    taxesPSF() {
      return isNaN(this.taxesCleansed) || this.comp.building.total_square_footage == 0
        ? 0
        : (this.taxesCleansed / this.comp.building.total_square_footage) * 100;
    },

    debtSubtotal() {
      return this.debtCost + this.operatingExpensesCleansed * 100 + this.taxesCleansed * 100;
    },

    debtSubtotalPSF() {
      return isNaN(this.debtSubtotal) || this.comp.building.total_square_footage == 0
        ? 0
        : this.debtSubtotal / this.comp.building.total_square_footage;
    },

    roiOnLLEquityPercentageCleansed() {
      const cleansed = Number.prototype.cleanseInteger(this.roiOnLLEquityPercentage);
      return isNaN(cleansed) ? 0 : cleansed / 100;
    },

    roiOnLLEquity() {
      return this.roiOnLLEquityPercentageCleansed * this.equityRequired;
    },

    roiOnLLEquityPSF() {
      return isNaN(this.roiOnLLEquity) || this.comp.building.total_square_footage == 0
        ? 0
        : this.roiOnLLEquity / this.comp.building.total_square_footage;
    },

    amortizedDownPaymentCleansed() {
      const cleansed = Number.prototype.cleanseInteger(this.amortizedDownPayment);
      return isNaN(cleansed) ? 0 : cleansed;
    },

    amortizedDownPaymentPSF() {
      return isNaN(this.amortizedDownPaymentCleansed) || this.comp.building.total_square_footage == 0
        ? 0
        : (this.amortizedDownPaymentCleansed / this.comp.building.total_square_footage) * 100;
    },

    equivalentLease() {
      return this.debtSubtotal + this.roiOnLLEquity + this.amortizedDownPaymentCleansed * 100;
    },

    equivalentLeasePSF() {
      return isNaN(this.equivalentLease) || this.comp.building.total_square_footage == 0
        ? 0
        : this.equivalentLease / this.comp.building.total_square_footage;
    }
  },

  methods: {
    save() {
      const compId = this.comp && this.comp.id ? this.comp.id : null;

      if (!compId) {
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Unable to save: Comp ID is missing'
        });
        return;
      }

      axios
        .post('/projects/' + this.comp.project_id + '/comps/' + compId + '/lease-vs-purchase-calculation', {
          comp_id: compId,
          sale_price: this.salePriceCleansed,
          sale_discount_percentage: Number.prototype.cleanseFloat(this.saleDiscountPercentage),
          economic_developer_discount_percentage: Number.prototype.cleanseFloat(this.economicDeveloperDiscountPercentage),
          closing_cost_percentage: Number.prototype.cleanseFloat(this.closingCostPercentage),
          renovation_costs: this.renovationCostsCleansed,
          other_costs: this.otherCostsCleansed,
          debt_service_interest_percentage: Number.prototype.cleanseFloat(this.debtServiceInterestPercentage),
          debt_service_lease_term_years: this.debtServiceLeaseTermYears,
          equity_percentage: Number.prototype.cleanseFloat(this.equityPercentage),
          operating_expenses: this.operatingExpensesCleansed,
          taxes: this.taxesCleansed,
          roi_on_ll_equity_percentage: Number.prototype.cleanseFloat(this.roiOnLLEquityPercentage),
          amortized_down_payment: this.amortizedDownPaymentCleansed
        })
        .then(response => {
          this.$notify({
            type: 'success',
            title: 'Success',
            text: response.data.message
          });
        })
        .catch(response => {
          console.error(response);

          this.$notify({
            type: 'error',
            title: 'Error',
            text: response.data.message
          });
        });
    }
  }
};
</script>
